/**
 * CandidatePage — full-page (non-modal) view of every candidate facet.
 *
 * Route: /app/recruit/candidate/:id
 *
 * Renders the same 4 tabs as the (retired) CandidateDrawer, but as a first-class
 * page: back-nav, no backdrop, wider layout, deep-linkable, browser-history-friendly.
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";
import i18n from "i18next";
import {
    ArrowLeft, Linkedin, Mail, MapPin, Building2, Loader2, Sparkles,
    Radar, Download, GitMerge, Search as SearchIcon, MessageCircle, Share2, Check,
    UsersRound, X, Plus, Pencil, Trash2,
} from "lucide-react";
import CandidateAvatar from "@/components/CandidateAvatar";
import { TrustBadge } from "@/components/common/TrustBadge";
import { LinkedInButton } from "@/components/common/LinkedInButton";
import {
    ProfileTab, SearchesTab, MatchesTab, OutreachTab,
} from "@/components/CandidateDrawer";
import { PageHeader } from "../Dashboard";

const TAB_META = [
    { id: "profile",  labelKey: "drawer.profile",     Icon: Sparkles },
    { id: "searches", labelKey: "drawer.searches",    Icon: SearchIcon },
    { id: "matches",  labelKey: "drawer.matches_tab", Icon: GitMerge },
    { id: "outreach", labelKey: "drawer.outreach",    Icon: MessageCircle },
];

export default function CandidatePage() {
    const { t } = useTranslation();
    const { id } = useParams();
    const nav = useNavigate();
    const [searchParams] = useSearchParams();
    const initialTab = TAB_META.some((tm) => tm.id === searchParams.get("tab")) ? searchParams.get("tab") : "profile";
    const [tab, setTab] = useState(initialTab);
    const [candidate, setCandidate] = useState(null);
    const [context, setContext] = useState(null);
    const [loading, setLoading] = useState(true);
    const [reverseHunting, setReverseHunting] = useState(false);
    const [showEdit, setShowEdit] = useState(false);
    const [deepEnriching, setDeepEnriching] = useState(false);
    const [copied, setCopied] = useState(false);
    const [showReferrals, setShowReferrals] = useState(false);

    const copyShareLink = async () => {
        const url = window.location.href;
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            toast.success(t("cand_page.link_copied_team"));
            setTimeout(() => setCopied(false), 2200);
        } catch (e) {
            // Clipboard API can fail in insecure contexts / restrictive browsers.
            // Fallback: legacy execCommand approach.
            try {
                const ta = document.createElement("textarea");
                ta.value = url; ta.setAttribute("readonly", "");
                ta.style.position = "absolute"; ta.style.left = "-9999px";
                document.body.appendChild(ta); ta.select();
                document.execCommand("copy"); document.body.removeChild(ta);
                setCopied(true);
                toast.success(t("cand_page.link_copied"));
                setTimeout(() => setCopied(false), 2200);
            } catch {
                toast.error(t("cand_page.copy_failed"));
            }
        }
    };

    useEffect(() => {
        if (!id) return;
        setLoading(true);
        Promise.all([
            api.get(`/candidates/${id}`),
            api.get(`/candidates/${id}/context`).catch(() => ({ data: { searches: [], matches: [], outreach: [] } })),
        ]).then(([a, b]) => {
            setCandidate(a.data);
            setContext(b.data);
        }).catch(() => toast.error(t("cand_page.not_found"))).finally(() => setLoading(false));
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    const startReverseHunt = async () => {
        if (!candidate) return;
        if (!confirm(t("cand_page.reverse_confirm", { name: candidate.name }))) return;
        setReverseHunting(true);
        try {
            const r = await api.post("/hunt-missions", {
                title: i18n.t("candpage.vacancies_for", { name: candidate.name }),
                prompt: `Vind bedrijven met openstaande rollen die matchen met ${candidate.name} (${candidate.title})`,
                mission_type: "candidate_to_vacancies",
                data_source: "perplexity",
                target_country: candidate.location?.split(",").slice(-1)[0]?.trim() || "BE",
                target_role: candidate.title,
                source_candidate_id: candidate.id,
                schedule: "manual",
                run_now: true,
            });
            toast.success(t("cand_page.mission_started", { n: r.data?.first_run?.signals_generated || 0 }));
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("cand_page.reverse_failed"));
        } finally { setReverseHunting(false); }
    };

    const deepEnrich = async () => {
        if (!candidate) return;
        setDeepEnriching(true);
        try {
            const r = await api.post(`/profile-resolver/deep-enrich/${candidate.id}`);
            const prof = r.data?.profile || {};
            const email = (prof.emails || [])[0];
            toast.success(email
                ? t("cand_page.deep_enrich_ok_email", {
                    n: (prof.experience || []).length, s: (prof.skills || []).length, email,
                })
                : t("cand_page.deep_enrich_ok", {
                    n: (prof.experience || []).length, s: (prof.skills || []).length,
                }));
            const fresh = await api.get(`/candidates/${candidate.id}`);
            setCandidate(fresh.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("cand_page.deep_enrich_failed"));
        } finally { setDeepEnriching(false); }
    };

    if (loading) {
        return (
            <PageHeader title={t("cand_page.candidate")}>
                <div className="p-10 text-center text-[var(--jr-text-2)]">
                    <Loader2 className="animate-spin mx-auto" size={20} />
                </div>
            </PageHeader>
        );
    }
    if (!candidate) {
        return (
            <PageHeader title={t("cand_page.candidate")}>
                <div className="p-10 text-center text-[var(--jr-error)]">
                    {t("cand_page.not_found")}{" "}
                    <button onClick={() => nav(-1)} className="underline text-[var(--jr-brand)]" data-testid="candidate-back">{t("cand_page.back")}</button>
                </div>
            </PageHeader>
        );
    }

    return (
        <div className="px-8 py-8 max-w-6xl mx-auto" data-testid="candidate-page">
            {/* Back nav */}
            <button
                onClick={() => nav(-1)}
                data-testid="candidate-back"
                className="inline-flex items-center gap-1.5 text-xs text-[var(--jr-text-2)] hover:text-white mb-5 font-mono uppercase tracking-[0.16em]">
                <ArrowLeft size={14} /> {t("cand_page.back")}
            </button>

            {/* Header card */}
            <div className="rounded-2xl border border-white/8 bg-[var(--jr-surface)] p-6 mb-4">
                <div className="flex items-start gap-5">
                    <CandidateAvatar name={candidate.name} url={candidate.photo_url} size={80} testid="page-avatar" />
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h1 className="font-display text-3xl tracking-tight text-white truncate">{candidate.name}</h1>
                            {candidate.talent_pool === "cronos" && (
                                <span className="text-[10px] font-mono uppercase tracking-[0.16em] px-2 py-0.5 rounded bg-[var(--jr-amber)]/20 text-[var(--jr-amber)] border border-[var(--jr-amber)]/30">
                                    Cronos
                                </span>
                            )}
                            {candidate.referred_by_name && (
                                <span className="text-[10px] font-mono uppercase tracking-[0.16em] px-2 py-0.5 rounded bg-[#a855f7]/20 text-[#a855f7] border border-[#a855f7]/30"
                                    data-testid="referred-by-badge"
                                    title={t("cand_page.referred_via", { name: candidate.referred_by_name })}>
                                    ← via {candidate.referred_by_name}
                                </span>
                            )}
                            {/* Anti-hallucinatie: trust-badge + verifieer-knop */}
                            <TrustBadge entity="candidate" entityId={candidate.id}
                                verification={candidate.verification}
                                onVerified={(ver) => setCandidate((c) => ({ ...c, verification: ver }))}
                                testid="candidate-trust-badge"/>
                        </div>
                        <div className="text-base text-[var(--jr-text-2)] mt-1">{candidate.title}</div>
                        <div className="text-xs text-[var(--jr-text-3)] mt-2 flex items-center gap-3 flex-wrap">
                            {candidate.current_company && (
                                <span className="inline-flex items-center gap-1"><Building2 size={12} />{candidate.current_company}</span>
                            )}
                            {candidate.location && (
                                <span className="inline-flex items-center gap-1"><MapPin size={12} />{candidate.location}</span>
                            )}
                            {candidate.seniority && (
                                <span className="text-[10px] font-mono uppercase tracking-[0.16em] px-2 py-0.5 rounded bg-white/5">{candidate.seniority}</span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Contact strip */}
                <div className="mt-5 flex items-center gap-2 flex-wrap">
                    <LinkedInButton url={candidate.linkedin_url} name={candidate.name} variant="pill" testid="page-linkedin" resolve={{ type: "candidate", id: candidate.id }} />
                    {candidate.linkedin_url && (
                        <button onClick={deepEnrich} disabled={deepEnriching}
                            data-testid="page-deep-enrich"
                            title={t("cand_page.deep_enrich_title")}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#97d700]/40 bg-[#97d700]/10 text-[#97d700] hover:bg-[#97d700]/20 text-xs disabled:opacity-50">
                            {deepEnriching ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                            {t("cand_page.deep_enrich")}
                        </button>
                    )}
                    {candidate.email && (
                        <a href={`mailto:${candidate.email}`}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[var(--jr-info)]/40 bg-[var(--jr-info)]/10 text-[var(--jr-info)] hover:bg-[var(--jr-info)]/20 transition-colors text-xs">
                            <Mail size={12} /> Email
                        </a>
                    )}
                    {candidate.cv_storage_path && (
                        <button onClick={async () => {
                            try {
                                const r = await api.get(`/talent-pool/${candidate.id}/cv`, { responseType: "blob" });
                                const url = URL.createObjectURL(r.data);
                                const a = document.createElement("a");
                                a.href = url; a.download = candidate.cv_original_filename || `${candidate.name}.pdf`;
                                a.click(); URL.revokeObjectURL(url);
                            } catch (e) { toast.error(t("pool.cv_download_fail")); }
                        }} data-testid="page-cv"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 text-[var(--jr-text-2)] hover:bg-white/5 text-xs">
                            <Download size={12} /> {t("drawer.download_cv")}
                        </button>
                    )}
                    <Link to={`/app/recruit/outreach?candidate=${candidate.id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 text-[var(--jr-text-2)] hover:bg-white/5 text-xs"
                        data-testid="page-outreach-link">
                        <MessageCircle size={12} /> {t("cand_page.new_outreach")}
                    </Link>

                    <button onClick={copyShareLink}
                        data-testid="page-share-link"
                        title={t("cand_page.share_title")}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs transition-colors ${
                            copied
                                ? "border-[#22c55e]/40 bg-[#22c55e]/10 text-[#22c55e]"
                                : "border-white/10 text-[var(--jr-text-2)] hover:bg-white/5"
                        }`}>
                        {copied ? <><Check size={12} /> {t("network.copied")}</> : <><Share2 size={12} /> {t("cand_page.share_link")}</>}
                    </button>

                    <button onClick={() => setShowReferrals(true)}
                        data-testid="page-referrals"
                        title={t("cand_page.referrals_title")}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#a855f7]/40 bg-[#a855f7]/10 text-[#a855f7] hover:bg-[#a855f7]/20 text-xs">
                        <UsersRound size={12} /> {t("cand_page.find_referrals")}
                    </button>

                    <button onClick={() => setShowEdit(true)}
                        data-testid="page-edit-candidate"
                        title={t("cand_edit.edit_title")}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 text-[var(--jr-text-2)] hover:bg-white/5 text-xs">
                        <Pencil size={12} /> {t("common.edit")}
                    </button>

                    <button onClick={async () => {
                        if (!window.confirm(t("cand_edit.delete_confirm", { name: candidate.name }))) return;
                        try {
                            await api.delete(`/candidates/${candidate.id}`);
                            toast.success(t("cand_edit.deleted_toast", { name: candidate.name }));
                            nav("/app/recruit/pool");
                        } catch (e) { toast.error(e?.response?.data?.detail || t("cand_edit.delete_failed")); }
                    }}
                        data-testid="page-delete-candidate"
                        title={t("cand_edit.delete_title")}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#ef4444]/40 bg-[#ef4444]/10 text-[#ef4444] hover:bg-[#ef4444]/20 text-xs">
                        <Trash2 size={12} /> {t("common.delete")}
                    </button>

                    <button onClick={startReverseHunt} disabled={reverseHunting}
                        data-testid="page-reverse-hunt"
                        title={t("cand_page.reverse_title")}
                        className="ml-auto inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[var(--jr-brand)] text-black text-xs font-medium hover:bg-[var(--jr-brand-2)] disabled:opacity-50">
                        {reverseHunting ? <Loader2 size={12} className="animate-spin" /> : <Radar size={12} />}
                        {t("drawer.reverse_hunt")}
                    </button>
                </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-white/8 mb-5 flex items-center gap-1 flex-wrap">
                {TAB_META.map((tm) => {
                    const active = tab === tm.id;
                    const Icon = tm.Icon;
                    const count = tm.id === "searches" ? (context?.searches?.length || 0)
                                : tm.id === "matches"  ? ((context?.matches?.length || 0) + (context?.vacancies?.length || 0))
                                : tm.id === "outreach" ? ((context?.outreach?.length || 0) + (context?.enrollments?.length || 0)) : null;
                    return (
                        <button key={tm.id} onClick={() => setTab(tm.id)}
                            data-testid={`page-tab-${tm.id}`}
                            className={`text-xs px-4 py-3 border-b-2 transition-colors inline-flex items-center gap-1.5 ${active
                                ? "border-[var(--jr-brand)] text-white"
                                : "border-transparent text-[var(--jr-text-3)] hover:text-white"}`}>
                            <Icon size={12} /> {t(tm.labelKey)}
                            {count != null && count > 0 && (
                                <span className={`text-[9px] font-mono px-1 py-0 rounded ${active ? "bg-[var(--jr-brand)] text-black" : "bg-white/8 text-[var(--jr-text-3)]"}`}>{count}</span>
                            )}
                        </button>
                    );
                })}
            </div>

            {/* Tab content — reuses drawer helpers */}
            <div className="rounded-2xl border border-white/8 bg-[var(--jr-surface)] p-6">
                {tab === "profile"  && <ProfileTab c={candidate} />}
                {tab === "searches" && <SearchesTab items={context?.searches || []} candidateId={candidate.id} />}
                {tab === "matches"  && <MatchesTab items={context?.matches || []} vacancies={context?.vacancies || []} />}
                {tab === "outreach" && <OutreachTab items={context?.outreach || []} candidateId={candidate.id} enrollments={context?.enrollments || []} />}
            </div>

            {showReferrals && (
                <ReferralModal candidate={candidate} onClose={() => setShowReferrals(false)} />
            )}

            {showEdit && (
                <EditCandidateModal candidate={candidate}
                    onClose={() => setShowEdit(false)}
                    onSaved={async () => {
                        setShowEdit(false);
                        try { const r = await api.get(`/candidates/${candidate.id}`); setCandidate(r.data); }
                        catch { /* houd huidige weergave */ }
                    }} />
            )}
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  EditCandidateModal — volledige profiel-CRUD (update) op de pagina  */
/* ------------------------------------------------------------------ */
/* Los veld-component (buiten de modal om focus-verlies bij re-render te voorkomen) */
function CandField({ label, value, onChange, type = "text", placeholder = "", testid }) {
    return (
        <div>
            <label className="text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)] block mb-1">{label}</label>
            <input type={type} value={value} onChange={onChange} placeholder={placeholder}
                data-testid={testid}
                className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[var(--jr-amber)] outline-none" />
        </div>
    );
}

function EditCandidateModal({ candidate, onClose, onSaved }) {
    const { t } = useTranslation();
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({
        name: candidate.name || "",
        title: candidate.title || "",
        current_company: candidate.current_company || "",
        location: candidate.location || "",
        seniority: candidate.seniority || "",
        years_experience: candidate.years_experience ?? "",
        availability: candidate.availability || "",
        available_from: candidate.available_from || "",
        email: candidate.email || "",
        phone: candidate.phone || "",
        linkedin_url: candidate.linkedin_url || "",
        skills: (candidate.skills || []).join(", "),
        languages: (candidate.languages || []).join(", "),
        summary: candidate.summary || "",
        pool_notes: candidate.pool_notes || "",
    });
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const save = async () => {
        if (!form.name.trim()) { toast.error(t("cand_edit.name_required")); return; }
        setBusy(true);
        try {
            const body = {
                name: form.name, title: form.title, current_company: form.current_company,
                location: form.location, seniority: form.seniority || null,
                email: form.email, phone: form.phone, linkedin_url: form.linkedin_url,
                availability: form.availability || null, available_from: form.available_from || null,
                summary: form.summary, pool_notes: form.pool_notes,
                skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean),
                languages: form.languages.split(",").map((s) => s.trim()).filter(Boolean),
            };
            if (form.years_experience !== "" && !isNaN(Number(form.years_experience))) {
                body.years_experience = Number(form.years_experience);
            }
            await api.patch(`/candidates/${candidate.id}`, body);
            toast.success(t("cand_edit.saved_toast"));
            onSaved?.();
        } catch (e) { toast.error(e?.response?.data?.detail || t("cand_edit.save_failed")); }
        finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center p-4"
            onClick={onClose} data-testid="cand-edit-modal">
            <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl border border-white/10 bg-[var(--jr-surface)] p-6"
                onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-5">
                    <h2 className="font-display text-xl text-white">{t("cand_edit.edit_title")}</h2>
                    <button onClick={onClose} className="p-1.5 rounded text-[var(--jr-text-3)] hover:text-white hover:bg-white/8" data-testid="cand-edit-close">
                        <X size={16} />
                    </button>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                    <CandField label={t("cand_edit.f_name")} value={form.name} onChange={set("name")} testid="cand-edit-name" />
                    <CandField label={t("cand_edit.f_title")} value={form.title} onChange={set("title")} testid="cand-edit-title" />
                    <CandField label={t("cand_edit.f_company")} value={form.current_company} onChange={set("current_company")} testid="cand-edit-current_company" />
                    <CandField label={t("cand_edit.f_location")} value={form.location} onChange={set("location")} testid="cand-edit-location" />
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)] block mb-1">{t("cand_edit.f_seniority")}</label>
                        <select value={form.seniority} onChange={set("seniority")} data-testid="cand-edit-seniority"
                            className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[var(--jr-amber)] outline-none">
                            <option value="">—</option>
                            {["junior", "medior", "senior", "lead", "executive"].map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </div>
                    <CandField label={t("cand_edit.f_years")} value={form.years_experience} onChange={set("years_experience")} testid="cand-edit-years_experience" type="number" />
                    <CandField label={t("cand_edit.f_available_from")} value={form.available_from} onChange={set("available_from")} testid="cand-edit-available_from" type="date" />
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)] block mb-1">{t("cand_edit.f_availability")}</label>
                        <select value={form.availability} onChange={set("availability")} data-testid="cand-edit-availability"
                            className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[var(--jr-amber)] outline-none">
                            <option value="">—</option>
                            <option value="available">{t("cand_edit.av_available")}</option>
                            <option value="open">{t("cand_edit.av_open")}</option>
                            <option value="passive">{t("cand_edit.av_passive")}</option>
                            <option value="unavailable">{t("cand_edit.av_unavailable")}</option>
                        </select>
                    </div>
                    <CandField label="Email" value={form.email} onChange={set("email")} testid="cand-edit-email" type="email" />
                    <CandField label={t("cand_edit.f_phone")} value={form.phone} onChange={set("phone")} testid="cand-edit-phone" />
                    <div className="sm:col-span-2"><CandField label="LinkedIn URL" value={form.linkedin_url} onChange={set("linkedin_url")} testid="cand-edit-linkedin_url" placeholder="https://linkedin.com/in/..." /></div>
                    <div className="sm:col-span-2"><CandField label={t("cand_edit.f_skills")} value={form.skills} onChange={set("skills")} testid="cand-edit-skills" placeholder="React, Python, ..." /></div>
                    <div className="sm:col-span-2"><CandField label={t("cand_edit.f_languages")} value={form.languages} onChange={set("languages")} testid="cand-edit-languages" placeholder="Nederlands, Engels, ..." /></div>
                    <div className="sm:col-span-2">
                        <label className="text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)] block mb-1">{t("cand_edit.f_summary")}</label>
                        <textarea rows={3} value={form.summary} onChange={set("summary")} data-testid="cand-edit-summary"
                            className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[var(--jr-amber)] outline-none" />
                    </div>
                    <div className="sm:col-span-2">
                        <label className="text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)] block mb-1">{t("cand_edit.f_notes")}</label>
                        <textarea rows={2} value={form.pool_notes} onChange={set("pool_notes")} data-testid="cand-edit-notes"
                            className="w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[var(--jr-amber)] outline-none" />
                    </div>
                </div>
                <div className="flex justify-end gap-2 mt-6">
                    <button onClick={onClose} className="px-4 py-2 rounded-full border border-white/10 text-sm text-[var(--jr-text-2)] hover:bg-white/5" data-testid="cand-edit-cancel">
                        {t("common.cancel")}
                    </button>
                    <button onClick={save} disabled={busy} data-testid="cand-edit-save"
                        className="px-5 py-2 rounded-full bg-[var(--jr-amber)] text-black text-sm font-medium hover:bg-[var(--jr-amber)]/85 disabled:opacity-50">
                        {busy ? <Loader2 size={13} className="animate-spin inline mr-1.5" /> : null}
                        {t("common.save")}
                    </button>
                </div>
            </div>
        </div>
    );
}

function ReferralModal({ candidate, onClose }) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [picked, setPicked] = useState(new Set());
    const [sameCompanyOnly, setSameCompanyOnly] = useState(false);
    const [creating, setCreating] = useState(false);

    const load = async () => {
        setLoading(true); setPicked(new Set());
        try {
            const r = await api.post(`/network/candidates/${candidate.id}/referrals`, {
                limit: 5, same_company_only: sameCompanyOnly,
            });
            setSuggestions(r.data?.suggestions || []);
            if (!r.data?.suggestions?.length) toast.info(t("cand_page.no_referrals"));
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("cand_page.generate_failed"));
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, [sameCompanyOnly, candidate.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const toggle = (idx) => setPicked((p) => {
        const n = new Set(p);
        n.has(idx) ? n.delete(idx) : n.add(idx);
        return n;
    });

    const createPicked = async () => {
        const picks = suggestions.filter((_, i) => picked.has(i));
        if (picks.length === 0) { toast.error(i18n.t("search.pick_min")); return; }
        setCreating(true);
        try {
            const r = await api.post(`/network/candidates/${candidate.id}/referrals/create`, { suggestions: picks });
            toast.success(i18n.t("candpage.referrals_added", { count: r.data.count }));
            onClose();
        } catch (e) {
            toast.error(e?.response?.data?.detail || i18n.t("companies.create_failed"));
        } finally { setCreating(false); }
    };

    return (
        <div className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6"
            onClick={onClose} data-testid="referral-modal-backdrop">
            <div className="w-full max-w-2xl rounded-2xl border border-white/10 bg-[var(--jr-surface)] p-6 max-h-[85vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()} data-testid="referral-modal">
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <div className="text-xs font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)]">{t("cand_page.referral_chain")}</div>
                        <div className="mt-1 text-lg font-display text-white">
                            {t("cand_page.referral_modal_title", { name: candidate.name })}
                        </div>
                        <div className="text-xs text-[var(--jr-text-3)] mt-1">
                            {t("cand_page.referral_modal_sub")}
                        </div>
                    </div>
                    <button onClick={onClose} data-testid="referral-close" className="text-[var(--jr-text-3)] hover:text-white p-1"><X size={18}/></button>
                </div>

                <div className="mt-4 flex items-center gap-2 text-xs">
                    <button onClick={() => setSameCompanyOnly((v) => !v)}
                        data-testid="referral-same-company-toggle"
                        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border transition-colors ${
                            sameCompanyOnly
                                ? "bg-[#a855f7]/15 text-[#a855f7] border-[#a855f7]/40"
                                : "text-[var(--jr-text-2)] border-white/10 hover:bg-white/5"
                        }`}>
                        {sameCompanyOnly ? <Check size={11}/> : null}
                        {t("cand_page.same_company")} ({candidate.current_company || "?"})
                    </button>
                    <button onClick={load} disabled={loading}
                        data-testid="referral-regenerate"
                        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-white/10 text-[var(--jr-text-2)] hover:bg-white/5">
                        {loading ? <Loader2 size={11} className="animate-spin"/> : <Sparkles size={11}/>}
                        {t("cand_page.regenerate")}
                    </button>
                </div>

                <div className="mt-4 space-y-2">
                    {loading && suggestions.length === 0 && (
                        <div className="text-center py-8 text-sm text-[var(--jr-text-3)]">
                            <Loader2 size={20} className="animate-spin inline mr-2"/> {t("cand_page.agent_searching")}
                        </div>
                    )}
                    {suggestions.map((s, i) => {
                        const on = picked.has(i);
                        return (
                            <button key={i} type="button" onClick={() => toggle(i)}
                                data-testid={`referral-pick-${i}`}
                                className={`w-full text-left rounded-lg border p-3 transition-colors ${
                                    on
                                        ? "border-[#a855f7] bg-[#a855f7]/5"
                                        : "border-white/10 bg-[var(--jr-surface-2)] hover:border-white/25"
                                }`}>
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm font-medium text-white">{s.name}</div>
                                        <div className="text-xs text-[var(--jr-text-2)] mt-0.5">
                                            {s.title || "?"} @ {s.company || "?"}
                                        </div>
                                        <div className="text-[10px] text-[var(--jr-text-3)] mt-0.5 flex items-center gap-2 flex-wrap">
                                            {s.location && <span className="inline-flex items-center gap-1"><MapPin size={9}/>{s.location}</span>}
                                            <LinkedInButton url={s.linkedin_url} name={s.name} variant="inline" resolve={s.id ? { type: "candidate", id: s.id } : undefined} />
                                        </div>
                                    </div>
                                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${on ? "bg-[#a855f7] border-[#a855f7]" : "border-white/20"}`}>
                                        {on && <Check size={12} className="text-white"/>}
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                </div>

                <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-white/8">
                    <button onClick={onClose} className="text-xs px-3 py-1.5 rounded-full border border-white/10 text-[var(--jr-text-2)] hover:bg-white/5">
                        Sluiten
                    </button>
                    <button onClick={createPicked} disabled={creating || picked.size === 0}
                        data-testid="referral-create"
                        className="text-xs px-4 py-1.5 rounded-full bg-[#a855f7] text-white font-medium hover:bg-[#a855f7]/85 disabled:opacity-50">
                        {creating ? <Loader2 size={11} className="animate-spin inline mr-1"/> : <Plus size={11} className="inline mr-1"/>}
                        Voeg {picked.size || 0} toe aan Cronos
                    </button>
                </div>
            </div>
        </div>
    );
}
